import { useCallback } from "react";
import {
  IconNameEnum,
  useDisruptionStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import { CoreDisruptionComponent } from "@b2bportal/core-types";
import { Icon } from "@b2bportal/core-ui";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import clsx from "clsx";
import defaultStyles from "./FlightDisruptionAlertBanner.module.scss";

export enum DisruptionAlertBannerType {
  DISRUPTED = "disrupted",
  BOOK_RETURN = "bookReturn",
  ENTRY_POINT = "entryPoint",
}

export interface FlightDisruptionAlertBannerProps {
  onClick: () => void;
  type: DisruptionAlertBannerType;
  productName?: string;
  destination?: string;
}

export const FlightDisruptionAlertBanner = ({
  onClick,
  type,
  productName,
  destination,
}: FlightDisruptionAlertBannerProps) => {
  const COMPONENT_KEY = CoreDisruptionComponent.FlightDisruptionAlertBanner;
  const styles = useDisruptionStyles(COMPONENT_KEY, defaultStyles);
  const [, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { t } = useI18nContext();

  const content = useCallback(() => {
    switch (type) {
      case DisruptionAlertBannerType.DISRUPTED:
        return (
          <div className={cn("text-container")}>
            <span className={cn("textFirstPart")}>
              {t(
                "core-disruption.disruptionUniversalExercise.flightDisruptionAlertBanner.flightDisruptedTitle"
              )}
            </span>
            <div className={cn("actionContainer")}>
              <span className={cn("textActionPart")}>
                {t(
                  "core-disruption.disruptionUniversalExercise.flightDisruptionAlertBanner.flightDisruptedAction",
                  {
                    productName: productName,
                  }
                )}
              </span>
              <Icon
                iconName={IconNameEnum.rightArrow}
                className={cn("arrow")}
              />
            </div>
          </div>
        );
      case DisruptionAlertBannerType.BOOK_RETURN:
        return (
          <div className={cn("text-container", [`isBookReturn`])}>
            <Trans
              i18nKey="core-disruption.disruptionUniversalExercise.flightDisruptionAlertBanner.bookReturnDetailed"
              components={{
                title: (
                  <div className={cn("textFirstPart", [`isBookReturn`])} />
                ),
                subtitle: (
                  <span className={cn("textSecondPart", [`isBookReturn`])} />
                ),
                rebook: (
                  <span className={cn("textActionPart", [`isBookReturn`])} />
                ),
              }}
              values={{
                destination: destination,
                productName: productName,
              }}
              className={cn("text-container")}
            />
            <div className={cn("action")}>
              <span className={cn("actionText")}>
                {t(
                  "core-disruption.disruptionUniversalExercise.flightDisruptionAlertBanner.rebookReturnFlight"
                )}
              </span>
              <Icon
                iconName={IconNameEnum.rightArrow}
                className={cn("arrow")}
              />
            </div>
          </div>
        );
      case DisruptionAlertBannerType.ENTRY_POINT:
        return (
          <div className={cn("text-container")}>
            <span className={cn("textFirstPart")}>
              {t(
                "core-disruption.disruptionUniversalExercise.flightDisruptionAlertBanner.entryPointTitle",
                {
                  productName: productName,
                }
              )}
            </span>
            <div className={cn("actionContainer")}>
              <span className={cn("textActionPart")}>
                {t(
                  "core-disruption.disruptionUniversalExercise.flightDisruptionAlertBanner.entryPointAction",
                  {
                    productName: productName,
                  }
                )}
              </span>
              <Icon
                iconName={IconNameEnum.rightArrow}
                className={cn("arrow")}
              />
            </div>
          </div>
        );
    }
  }, [cn, destination, productName, type, t]);

  return (
    <button
      className={cn("button-container")}
      onClick={(event: React.MouseEvent) => {
        event.stopPropagation();
        onClick();
      }}
    >
      <div className={clsx(cn("container"), cn(type))}>
        <div className={cn("content")}>{content()}</div>
      </div>
    </button>
  );
};

export default FlightDisruptionAlertBanner;
